// src/lib/janelaCobranca.ts

/**
 * Converte uma string de data vinda do Postgres em `Date` no fuso LOCAL.
 *
 * `new Date('2026-09-15')` é interpretado pelo JS como meia-noite **UTC** — que no
 * horário de Brasília é 14/09 às 21h. Quem lê o resultado com `getDate()` (local)
 * enxerga o dia 14, não o 15. Colunas `date` do Postgres (`data_vencimento`,
 * `leitura_atual`, `data_apresentacao`) chegam exatamente nesse formato, e o erro
 * de 1 dia se propagava para toda a Janela de Faturamento: `criticoJanelaIso` de
 * uma fatura com vencimento 15/09 saía 07/09 em vez de 08/09.
 *
 * Strings com hora (`2026-09-15T10:15:00Z`) já representam um instante sem
 * ambiguidade e continuam por `new Date()`.
 */
export const paraDataLocal = (isoStr: string): Date => {
  const soData = /^\d{4}-\d{2}-\d{2}$/.test(isoStr.trim());
  if (!soData) return new Date(isoStr);
  const [y, m, d] = isoStr.trim().split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Avança a data para a próxima segunda-feira se cair no final de semana (sábado ou domingo).
 */
export const adjustForWeekend = (date: Date): Date => {
  const adjusted = new Date(date);
  const day = adjusted.getDay();
  // 0 = Sunday, 6 = Saturday
  if (day === 6) {
    adjusted.setDate(adjusted.getDate() + 2); // Move to Monday
  } else if (day === 0) {
    adjusted.setDate(adjusted.getDate() + 1); // Move to Monday
  }
  return adjusted;
};

/**
 * Adiciona N dias a uma data e ajusta finais de semana se necessário.
 */
export const addDaysAndAdjust = (isoStr: string | null, days: number): Date | null => {
  if (!isoStr) return null;
  const d = paraDataLocal(isoStr);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return adjustForWeekend(d);
};

/**
 * Soma N dias a uma data ISO (YYYY-MM-DD) e retorna no mesmo formato (YYYY-MM-DD).
 * Faz o parsing em data local (split em '-') para evitar shift de fuso horário
 * (uma string 'YYYY-MM-DD' parseada por `new Date()` vira meia-noite UTC e pode
 * recuar 1 dia no horário do Brasil). Sem ajuste de fim de semana.
 */
export const addDaysISO = (isoStr: string | null | undefined, days: number): string => {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return '';
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/**
 * Adiciona 1 mês a uma data e ajusta finais de semana se necessário.
 */
export const addOneMonthAndAdjust = (isoStr: string | null): Date | null => {
  return addMonthsAndAdjust(isoStr, 1);
};

/**
 * Adiciona X meses a uma data e ajusta finais de semana se necessário.
 */
export const addMonthsAndAdjust = (isoStr: string | null, months: number): Date | null => {
  if (!isoStr) return null;
  const d = paraDataLocal(isoStr);
  if (isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + months);
  return adjustForWeekend(d);
};

/**
 * Adiciona 1 mês, subtrai dias e ajusta finais de semana se necessário.
 */
export const addOneMonthSubDaysAndAdjust = (isoStr: string | null, subDays: number): Date | null => {
  return addMonthsSubDaysAndAdjust(isoStr, 1, subDays);
};

/**
 * Adiciona X meses, subtrai dias e ajusta finais de semana se necessário.
 */
export const addMonthsSubDaysAndAdjust = (isoStr: string | null, months: number, subDays: number): Date | null => {
  if (!isoStr) return null;
  const d = paraDataLocal(isoStr);
  if (isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + months);
  d.setDate(d.getDate() - subDays);
  return adjustForWeekend(d);
};

export const getMonthDiff = (expectedMonthStr: string, refMonthStr: string): number => {
  const mapMes = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
  
  const parseMonthAndYear = (str: string): { m: number, y: number } => {
    if (!str) return { m: 1, y: 2026 };
    const parts = str.split('/');
    if (parts.length !== 2) return { m: 1, y: 2026 };
    
    let m = parseInt(parts[0], 10);
    if (isNaN(m)) {
      const index = mapMes.indexOf(parts[0].toUpperCase());
      m = index !== -1 ? index + 1 : 1;
    }
    const y = parseInt(parts[1], 10) || 2026;
    return { m, y };
  };

  const p1 = parseMonthAndYear(expectedMonthStr);
  const p2 = parseMonthAndYear(refMonthStr);
  
  return (p1.y - p2.y) * 12 + (p1.m - p2.m);
};

const MES_REFERENCIA_VALIDO = /^\s*([A-Za-z]{3}|\d{1,2})\/\d{4}\s*$/;

/**
 * Ordena as faturas candidatas a BASE da previsão de janela de um mês que ainda
 * não tem fatura — a primeira da lista é a base.
 *
 * A ordem é pela proximidade do mês de referência ao mês previsto, não pela data
 * de criação da linha. Até 15/09/2026 a base era a fatura criada por último, e
 * baixar um mês atrasado a transformava em base do mês corrente. Caso real: UC
 * 337787501263 — o robô baixou JUL/2026 em 15/09 e a previsão de SET/2026 passou
 * a partir de JUL (2 ciclos de distância) em vez de AGO (1 ciclo), acumulando o
 * desvio de um ciclo irregular a mais.
 *
 * 1. Meses ATÉ o previsto, do mais próximo para o mais antigo.
 * 2. Meses DEPOIS do previsto (mês pendente atrasado com fatura posterior já no
 *    sistema), do mais próximo para o mais distante — só servem na falta de (1).
 * 3. Mês de referência ilegível por último.
 * Empate (duas linhas do mesmo mês): a criada por último.
 */
export const ordenarFaturasBasePrevisao = <T extends { mes_referencia?: string | null; created_at?: string | null }>(
  faturas: T[],
  mesPrevisto: string,
): T[] => {
  const rank = (f: T): [number, number] => {
    if (!f.mes_referencia || !MES_REFERENCIA_VALIDO.test(f.mes_referencia)) return [2, 0];
    const diff = getMonthDiff(mesPrevisto, f.mes_referencia);
    return diff >= 0 ? [0, diff] : [1, -diff];
  };
  const criado = (f: T) => (f.created_at ? new Date(f.created_at).getTime() : 0);
  return [...faturas].sort((a, b) => {
    const [ga, da] = rank(a);
    const [gb, db] = rank(b);
    return ga - gb || da - db || criado(b) - criado(a);
  });
};

/**
 * Início previsto da janela: dia seguinte à `proxima_leitura` da fatura base,
 * deslocado pelos ciclos entre a base e o mês previsto. A base do mês anterior
 * (`mesesAteMesPrevisto` = 1) já aponta para o ciclo certo; base mais antiga anda
 * para frente e base de mês POSTERIOR anda para trás.
 */
export const inicioJanelaPrevista = (proximaLeitura: string | null, mesesAteMesPrevisto: number): Date | null => {
  const base = addDaysAndAdjust(proximaLeitura, 1);
  if (!base || isNaN(base.getTime())) return null;
  if (mesesAteMesPrevisto === 1) return base;
  return addMonthsAndAdjust(base.toISOString(), mesesAteMesPrevisto - 1);
};

/**
 * Calcula a diferença de dias entre duas datas (DataFinal - DataInicial).
 *
 * Compara DIAS DE CALENDÁRIO no fuso local: 'hoje 23h' e 'amanhã 01h' distam 1 dia,
 * não 0. A normalização é local (e não UTC como até 03/09/2026) para acompanhar o
 * `paraDataLocal` acima — com parsing local e getters UTC, dois horários do mesmo
 * dia em Brasília perto da meia-noite caíam em datas UTC diferentes.
 */
export const diffInDays = (endIso: string | null, startIso: string | null): number | null => {
  if (!endIso || !startIso) return null;
  const end = paraDataLocal(endIso);
  const start = paraDataLocal(startIso);
  if (isNaN(end.getTime()) || isNaN(start.getTime())) return null;

  // Normaliza para meia-noite local antes de dividir, senão o horário de verão
  // (ou um par de datas com horas diferentes) devolve fração de dia.
  const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());

  return Math.floor((utcEnd - utcStart) / (1000 * 60 * 60 * 24));
};

/**
 * Transforma uma data ajustada em string 'dd/MM/yyyy'.
 */
export const formatDateString = (d: Date | null): string => {
  if (!d) return '--/--/----';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Formata um ISO para 'dd/MM/yyyy' com ajuste opcional
 */
export const formatIsoWithAdjust = (isoStr: string | null): string => {
  if (!isoStr) return '--/--/----';
  const d = paraDataLocal(isoStr);
  if (isNaN(d.getTime())) return '--/--/----';
  return formatDateString(adjustForWeekend(d));
};
